/**
 * Commercial — the business-accounts side, in one column.
 *
 * Owner, Sep 28 2026: "make a commercial side profile." Like the Field page is
 * his day outside, this is his day with the business accounts: the visits to
 * their sites this week and whether each was reported, what needs a hand
 * (a site with no contact, a COI running out, a contract ending, a report
 * that failed, a visit never reported), the bids out to them, the money they
 * owe — and the accounts themselves. The hat switcher offers it beside Field
 * and Admin.
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Building2, ChevronRight, MapPin, AlertTriangle, FileText, DollarSign } from 'lucide-react';
import { api } from '../services/api';
import { money } from '../utils/format';
import { dayLabel } from '../utils/commercial';
import { accountWarnings, pricingLabel, CommercialRow } from './CommercialAccounts';
import ProposalPaste from '../components/ProposalPaste';

const dayWords = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
const fmtTime = (t?: string | null) => {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};
const STATUS: Record<string, string> = { complete: 'Done', in_progress: 'On site', scheduled: 'Booked', skipped: 'Skipped', canceled: 'Cancelled', cancelled: 'Cancelled' };

function Section({ title, count, action, children }: { title: string; count?: number; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="card p-4 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-black uppercase tracking-tight text-gray-900">{title}{count != null ? <span className="ml-1.5 text-gray-500 font-bold">· {count}</span> : null}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export default function CommercialToday() {
  const navigate = useNavigate();
  const [paste, setPaste] = React.useState(false);
  const { data, isLoading, isError } = useQuery('commercial-today', () => api.get('/commercial/today').then(r => r.data), { staleTime: 30_000 });
  const { data: accts } = useQuery(['commercial'], () => api.get('/commercial').then(r => r.data.accounts as CommercialRow[]));
  const accounts = accts || [];

  if (isLoading) return <div className="card p-6 text-sm text-gray-500">Loading the accounts…</div>;
  if (isError || !data) return <div className="card p-6 text-sm text-amber-700">Could not load the commercial side.</div>;

  const visits: any[] = data.visits || [];
  const byDay = visits.reduce<Record<string, any[]>>((m, v) => { (m[v.date] = m[v.date] || []).push(v); return m; }, {});
  const days = Object.keys(byDay).sort();
  const hands: { key: string; text: string; bad: boolean; href: string }[] = [];
  for (const a of accounts) for (const w of accountWarnings(a)) hands.push({ key: `${a.id}:${w.text}`, text: `${a.company_name || a.name}: ${w.text}`, bad: w.bad, href: `/commercial/${a.id}` });
  for (const s of data.sites_without_contact || []) hands.push({ key: `site:${s.id}`, text: `${s.company}: ${s.site} has no site contact — reports go nowhere`, bad: false, href: `/commercial/${s.customer_id}` });
  for (const u of data.unreported || []) hands.push({ key: `unrep:${u.job_id}`, text: `${u.company}: ${dayLabel(u.date)} at ${u.site} was never reported`, bad: false, href: `/commercial/${u.customer_id}` });
  const bids: any[] = data.bids || [];
  const owed = accounts.filter(a => Number(a.open_balance) > 0).sort((a, b) => Number(b.open_balance) - Number(a.open_balance));
  const owedTotal = owed.reduce((t, a) => t + Number(a.open_balance || 0), 0);

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-brand-700">Commercial</p>
          <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900 leading-none mt-1">{dayWords(data.today)}</h1>
          <p className="text-sm text-gray-600 mt-1">{accounts.length} account{accounts.length === 1 ? '' : 's'} · {data.sites_count || 0} site{data.sites_count === 1 ? '' : 's'}{data.overdue_count ? ` · ${data.overdue_count} bill${data.overdue_count === 1 ? '' : 's'} overdue` : ''}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setPaste(true)} className="btn-secondary text-sm">Paste a proposal</button>
          <Link to="/commercial" className="btn-primary text-sm">Accounts</Link>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        {[
          { label: 'This week', value: String(visits.length), sub: `visit${visits.length === 1 ? '' : 's'} · ${visits.filter(v => v.status === 'complete').length} done`, tone: '' },
          { label: 'Needs a hand', value: String(hands.length), sub: hands.some(h => h.bad) ? `${hands.filter(h => h.bad).length} urgent` : hands.length ? 'none urgent' : 'all clear', tone: hands.some(h => h.bad) ? 'text-red-700 font-bold' : hands.length ? 'text-amber-700 font-bold' : 'text-emerald-700 font-semibold' },
          { label: 'Bids out', value: String(bids.filter(b => b.status === 'sent').length), sub: `${bids.filter(b => b.status === 'draft').length} draft · ${bids.filter(b => b.status === 'accepted').length} signed`, tone: '' },
          { label: 'They owe', value: money(owedTotal), sub: data.overdue_count ? `${data.overdue_count} bill${data.overdue_count === 1 ? '' : 's'} overdue` : owed.length ? 'within terms' : 'nothing open', tone: data.overdue_count ? 'text-red-700 font-bold' : '' },
        ].map(k => (
          <div key={k.label} className="card p-3.5 min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">{k.label}</p>
            <p className="text-xl font-black text-gray-900 tabular-nums truncate mt-0.5">{k.value}</p>
            <p className={`text-xs truncate ${k.tone || 'text-gray-600'}`}>{k.sub}</p>
          </div>
        ))}
      </div>

      <Section title="This week at their sites" count={visits.length}>
        {!visits.length ? <p className="text-sm text-gray-600">No visits to a business site booked in the next seven days.</p> : days.map(d => (
          <div key={d}>
            <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mt-2 mb-1">{d === data.today ? 'Today' : dayWords(d)}</p>
            <ul className="divide-y divide-gray-100">
              {byDay[d].map(v => (
                <li key={v.id} className="py-2 flex items-start gap-3">
                  <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900 truncate">{v.site} <span className="font-normal text-gray-500">· {v.company}</span></p>
                    <p className="text-xs text-gray-600">{[v.title, fmtTime(v.time_start), v.crew || v.route || 'nobody assigned yet'].filter(Boolean).join(' · ')}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${v.status === 'complete' ? 'bg-emerald-50 text-emerald-800' : v.status === 'in_progress' ? 'bg-blue-50 text-blue-800' : 'bg-gray-100 text-gray-700'}`}>{STATUS[v.status] || v.status}</span>
                    {v.status === 'complete' && <p className={`text-[11px] mt-0.5 ${v.report === 'sent' ? 'text-emerald-700' : v.report === 'failed' ? 'text-red-600' : 'text-amber-700'}`}>{v.report === 'sent' ? 'reported' : v.report === 'failed' ? 'report failed' : 'not reported'}</p>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </Section>

      <Section title="Needs a hand" count={hands.length}>
        {!hands.length ? <p className="text-sm text-gray-600">Nothing. Contacts on every site, papers in date, every visit reported.</p> : (
          <ul className="space-y-1">
            {hands.map(h => (
              <li key={h.key}>
                <Link to={h.href} className={`flex items-center gap-2 text-sm ${h.bad ? 'text-red-700' : 'text-amber-800'}`}>
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" /><span className="flex-1">{h.text}</span><ChevronRight className="h-4 w-4 text-gray-400" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Bids out to them" count={bids.length} action={<Link to="/quotes" className="text-xs font-bold text-brand-700 underline">All bids</Link>}>
        {!bids.length ? <p className="text-sm text-gray-600">No open bids on a business account. Paste a proposal to start one.</p> : (
          <ul className="divide-y divide-gray-100">
            {bids.map(b => (
              <li key={b.id}>
                <button type="button" onClick={() => navigate('/quotes', { state: { openQuoteId: b.id } })} className="w-full text-left py-2 flex items-center gap-3">
                  <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900 truncate">{b.title} <span className="font-normal text-gray-500">· {b.company}</span></p>
                    <p className="text-xs text-gray-600">{b.status === 'sent' ? `sent ${dayLabel(String(b.sent_at || '').slice(0, 10))}` : b.status === 'accepted' ? `accepted ${dayLabel(String(b.accepted_at || '').slice(0, 10))}` : 'draft — not sent yet'}{b.one_time > 0 ? ` · ${money(b.one_time)} one-time` : ''}{b.per_visit ? ` · ${b.per_visit}` : ''}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="They owe" count={owed.length} action={owedTotal > 0 ? <span className="text-sm font-black text-gray-900"><DollarSign className="inline h-4 w-4 -mt-0.5" />{money(owedTotal).replace('$', '')}</span> : undefined}>
        {!owed.length ? <p className="text-sm text-gray-600">Nothing outstanding on a business account.</p> : (
          <ul className="divide-y divide-gray-100">
            {owed.map(a => (
              <li key={a.id}>
                <Link to={`/commercial/${a.id}`} className="py-2 flex items-center gap-3">
                  <div className="min-w-0 flex-1"><p className="text-sm font-semibold text-gray-900 truncate">{a.company_name || a.name}</p><p className="text-xs text-gray-600">{pricingLabel(a)} · net {a.payment_terms ?? 30}</p></div>
                  <span className="text-sm font-bold text-gray-900">{money(a.open_balance)}</span>
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Accounts" count={accounts.length}>
        {!accounts.length ? <p className="text-sm text-gray-600">No business accounts yet.</p> : (
          <ul className="divide-y divide-gray-100">
            {accounts.map(a => (
              <li key={a.id}>
                <Link to={`/commercial/${a.id}`} className="py-2 flex items-center gap-3">
                  <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900 truncate">{a.company_name || a.name}</p>
                    <p className="text-xs text-gray-600">{a.sites} site{a.sites === 1 ? '' : 's'} · {pricingLabel(a)}{a.next_visit ? ` · next ${dayLabel(a.next_visit)}` : ' · nothing booked'}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {paste && <ProposalPaste onClose={() => setPaste(false)} />}
    </div>
  );
}
